/**
 * Lets a nested grid put its controls on the page's toolbar, beside the search.
 *
 * The Cards, Binder and Explore screens draw zoom and sort to the right of the
 * search field, on the same row. Every surface built for Wish drew its own row
 * of controls under its title instead, which is a second arrangement of the same
 * two buttons, costs a whole row of height, and separates them from the search
 * they belong beside. The owner: "where is our Zoom option? where is our sort
 * option?... that should be next to the search bar like the binder screen".
 *
 * Those surfaces are several components deep inside the Collection page, so the
 * controls have to travel up rather than down. A surface registers what it
 * offers while it is mounted; the page's toolbar renders whatever is registered
 * and falls back to its own controls when nothing is. Unregistering on unmount
 * is what makes backing out of a set restore the screen underneath.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';

const GridControlsContext = createContext(null);

export function GridControlsProvider({ children }) {
  const [controls, setControls] = useState(null);

  const register = useCallback((next) => setControls(next), []);
  const unregister = useCallback(() => setControls(null), []);

  const value = useMemo(() => ({ controls, register, unregister }), [controls, register, unregister]);
  return <GridControlsContext.Provider value={value}>{children}</GridControlsContext.Provider>;
}

/** For the toolbar: what a nested grid has offered, or null. */
export function useGridControls() {
  return useContext(GridControlsContext)?.controls ?? null;
}

/**
 * For a nested grid: offer these controls while mounted.
 *
 * `deps` is the caller's, because the values here are closures over state that
 * changes on every tap - a registration that never refreshed would leave the
 * toolbar calling last render's handlers.
 */
export function useRegisterGridControls(controls, deps = []) {
  const ctx = useContext(GridControlsContext);
  const register = ctx?.register;
  const unregister = ctx?.unregister;

  useEffect(() => {
    if (!register) return undefined;
    register(controls);
    return () => unregister?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [register, unregister, ...deps]);
}

export default GridControlsContext;
